import { Injectable } from '@angular/core';
import { CapacitorWifi } from '@capgo/capacitor-wifi';
import { Capacitor } from '@capacitor/core';
import type { Observable } from 'rxjs';
import { filter, from, interval, map, scan, switchMap, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CannesWifiService {
  private readonly VERIFY_TIMEOUT_MS = 12_000;
  private readonly VERIFY_POLL_INTERVAL_MS = 1_000;
  // @capgo/capacitor-wifi v8 exposes no native disconnect/connection-change event (only
  // `networksScanned`, Android scan) — we must detect disconnection by polling the live SSID.
  private readonly DISCONNECT_POLL_INTERVAL_MS = 5_000;
  // getSsid() can reject transiently on iOS while still associated — require consecutive
  // misses so a single hiccup doesn't trigger a false disconnect.
  private readonly DISCONNECT_MISS_THRESHOLD = 2;

  private readonly PASS_PHRASE_ALLOWED_CHARS = /^[!-~]+$/;
  private readonly PASS_PHRASE_FORBIDDEN_CHARS = /["#=\\{}]/;

  /**
   * ASCII [33–126] excluding " # = \ { } — per device wiki and IEEE 802.11i-2004 Annex H.4.1.
   * Excluded chars are rejected by the device's wpa_supplicant config parser.
   * Rejection sampling avoids modulo bias.
   */
  generatePassphrase(): string {
    // Build alphabet: ASCII 33–126 minus excluded chars
    const excluded = new Set(['"', '#', '=', '\\', '{', '}']);
    const alphabet = Array.from({ length: 94 }, (_, index) => String.fromCharCode(index + 33))
      .filter(c => !excluded.has(c))
      .join('');
    const length = 15;

    const maxUnbiased = 256 - (256 % alphabet.length);
    let passphrase = '';
    while (passphrase.length < length) {
      const [byte] = crypto.getRandomValues(new Uint8Array(1));
      if (byte < maxUnbiased) {
        passphrase += alphabet[byte % alphabet.length];
      }
    }

    return passphrase;
  }

  isReusablePassphrase(passphrase: string | undefined): passphrase is string {
    if (!passphrase) return false;
    if (passphrase.length < 8 || passphrase.length > 63) return false;
    if (!this.PASS_PHRASE_ALLOWED_CHARS.test(passphrase)) return false;
    if (this.PASS_PHRASE_FORBIDDEN_CHARS.test(passphrase)) return false;
    return true;
  }

  // Both Android and iOS require location permission to read the current SSID.
  async hasLocationPermission(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return true;
    }
    const status = await CapacitorWifi.checkPermissions();
    return status.location === 'granted';
  }

  // Requires NSLocationWhenInUseUsageDescription in Info.plist.
  async requestLocationPermission(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const status = await CapacitorWifi.requestPermissions();
    if (status.location !== 'granted') {
      throw new Error('Location permission denied');
    }
  }

  /**
   * iOS: NEHotspotConfigurationManager.apply resolves when the *configuration* is registered, NOT
   * when the device has associated — a failed join silently falls back to the previous network.
   * We must verify the live SSID to avoid a false "connected". Verification requires both the
   * "com.apple.developer.networking.wifi-info" entitlement and location When-In-Use authorization.
   */
  async connectToCannesAp(ssid: string, passphrase?: string): Promise<void> {
    await this.requestLocationPermission();
    try {
      await CapacitorWifi.connect({
        ssid,
        password: passphrase,
        autoRouteTraffic: true,
      });
    } catch (error) {
      // iOS rejects with NEHotspotConfigurationError.alreadyAssociated when the phone is already
      // on that SSID — the configuration from an earlier run survives until it is removed. That is
      // the desired end state, not a failure, and no join prompt appears. Any other error is real.
      if (!this.isAlreadyAssociatedError(error)) {
        throw error;
      }
    }
    await this.verifyAssociated(ssid);
  }

  private isAlreadyAssociatedError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String((error as { message?: string })?.message ?? error);
    return message.toLowerCase().includes('already associated');
  }

  /**
   * Disconnects the phone from the Cannes AP. iOS only removes networks that
   * were added via this plugin — which is exactly how connectToCannesAp joins.
   */
  async disconnectFromCannesAp(): Promise<void> {
    await CapacitorWifi.disconnect();
  }

  watchDisconnect$(expectedSsid: string): Observable<void> {
    return interval(this.DISCONNECT_POLL_INTERVAL_MS).pipe(
      switchMap(() => from(this.checkAssociation(expectedSsid))),
      scan((misses, status) => {
        if (status === 'associated') return 0;
        if (status === 'lost') return misses + 1;
        return misses; // 'unknown' → hold the counter so SSID flapping doesn't trigger a false drop
      }, 0),
      filter(misses => misses >= this.DISCONNECT_MISS_THRESHOLD),
      map(() => {}),
      take(1)
    );
  }

  private async checkAssociation(expectedSsid: string): Promise<'associated' | 'lost' | 'unknown'> {
    try {
      const { ssid } = await CapacitorWifi.getSsid();
      if (ssid === expectedSsid) return 'associated';
      // Android returns "<unknown ssid>" (or empty) transiently while still associated — not a
      // real disconnect. A different, named network means we genuinely left the AP.
      if (!ssid || ssid === '<unknown ssid>') return 'unknown';
      return 'lost';
    } catch {
      return 'lost';
    }
  }

  private async verifyAssociated(expectedSsid: string): Promise<void> {
    const start = Date.now();
    let lastError: unknown = 'SSID never matched';

    while (Date.now() - start < this.VERIFY_TIMEOUT_MS) {
      try {
        const { ssid } = await CapacitorWifi.getSsid();
        if (ssid === expectedSsid) return;
        lastError = `connected to "${ssid}" instead of "${expectedSsid}"`;
      } catch (error) {
        // Without the wifi-info entitlement getSsid rejects (nehelper code 1) — can't verify.
        lastError = error;
      }
      await new Promise(resolve => setTimeout(resolve, this.VERIFY_POLL_INTERVAL_MS));
    }

    throw new Error(`Not associated with "${expectedSsid}" within ${this.VERIFY_TIMEOUT_MS}ms: ${String(lastError)}`);
  }
}
